// Open-state broadcast for the bug-report panel, plus the pure
// decision predicate the activity dock uses to know whether to
// auto-collapse on open. Lives in `src/lib` (not under any single
// component) because the publisher (bug-report widgets) and the
// subscriber (activity dock) are in different feature folders and
// should not import each other.
//
// Resolves the "Open follow-ups" item in
// docs/planning/bug_report_placement_v2.md:
//   "Whether the dock should auto-collapse when the bug-report modal
//    is open to prevent overlap. Likely yes for desktop."
//
// Pattern mirrors `ACTIVITY_DOCK_OPEN_EVENT` in
// `src/components/activity/ActivityFloatingDock.tsx` — a stable
// string dispatched on `window`, importable as a constant so
// publishers and subscribers don't share component imports. We use
// `CustomEvent` with `detail` rather than a bare `Event` because the
// dock's collapse rule depends on which platform variant fired
// (desktop fires "collapse"; mobile does not, because the mobile
// drawer fully occludes the dock anyway and collapsing the dock tab
// would be redundant chrome churn).

export const BUG_REPORT_PANEL_STATE_EVENT = "bb-bug-report-panel-state";

export type BugReportPanelSource = "desktop" | "mobile";

export type BugReportPanelStateEventDetail = {
  /** True when the panel just opened, false when it just closed.
   *  Edge-triggered: publishers should only fire on transitions, not
   *  on every render. */
  open: boolean;
  /** Which entry surfaced the panel. Desktop fires "collapse-the-dock"
   *  rules; mobile is informational only today (the mobile drawer
   *  occludes the entire viewport, so collapsing the dock tab is a
   *  no-op visually). Kept as a discriminator so future subscribers
   *  can differentiate without sniffing window sizes. */
  source: BugReportPanelSource;
};

/** Fire the broadcast event. SSR-safe: a no-op when `window` is
 *  absent. Publishers should call this on the open/closed *transition*
 *  only — emitting on every render would generate redundant noise
 *  for any subscriber that doesn't dedup. */
export function emitBugReportPanelStateEvent(
  detail: BugReportPanelStateEventDetail,
): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<BugReportPanelStateEventDetail>(
      BUG_REPORT_PANEL_STATE_EVENT,
      { detail },
    ),
  );
}

/** Pure predicate the activity dock uses to decide whether to
 *  auto-collapse on receiving a panel-state event. Codifies the
 *  locked answer from bug_report_placement_v2.md:
 *  desktop-only, only on open (we never auto-re-open on close —
 *  same discipline as the mobile-keyboard collapse pattern in
 *  `useActivityDockKeyboardCollapse.ts`).
 *
 *  We intentionally do NOT gate on "is the dock currently open?"
 *  here — `setOpen(false)` is idempotent at the caller, and pushing
 *  that decision to the predicate would require a stale-prone read
 *  of dock state through the broadcast channel. Keeping the
 *  predicate purely on the event payload makes it trivially testable
 *  and decouples the dock's lifecycle from the bug-report side. */
export function shouldCollapseDockOnBugReportOpen(
  detail: BugReportPanelStateEventDetail,
): boolean {
  return detail.open === true && detail.source === "desktop";
}
